/**
 * PlanCheckIntakeModal — AI-assisted plan check letter intake (CT-048 Task 5)
 *
 * Opens full-screen over the app. Phases:
 *   loading    — checking Supabase for an existing in-progress intake
 *   upload     — user picks/drops a PDF
 *   uploading  — uploading to Supabase Storage
 *   processing — Claude is extracting comments
 *   reviewing  — user confirms/tweaks items before committing
 *   committing — writing to plan_check_letters + plan_check_comments
 *
 * Persistence: items auto-save to plan_check_intakes (Supabase) so the user
 * can close and resume. On open, checks for an existing reviewing intake first.
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import {
  getIntakeForReview,
  getPCIntakeUploadUrl,
  uploadIntakePdf,
  processPCIntake,
  savePCIntake,
  commitPCIntake,
} from '../services/planCheck.js'
import { alertDialog } from './Dialog.jsx'

const DISCIPLINES = ['General','Architectural','Civil','Structural','Mechanical','Electrical','Plumbing','Energy','Landscape','Interior']

const PROCESSING_MESSAGES = [
  'Reading the letter…',
  'Parsing the comment structure…',
  'Identifying plan check comments…',
  'Sorting comments by discipline…',
  'Reviewing code section references…',
  'Flagging informational notices…',
  'Checking for backcheck appointment offers…',
  'Scanning for conditional approval language…',
  'Categorizing structural and civil items…',
  'Drafting FYI responses…',
  'Cross-referencing sheet callouts…',
  'Noting correction items by code section…',
  'Organizing by response complexity…',
  'Nearly there — assembling the review…',
]

// These loop once the main sequence is exhausted
const PATIENCE_MESSAGES = [
  'This one is thorough — still working…',
  'Large letters take a little longer…',
  'Almost done…',
  'Finalizing the extraction…',
  'Just a moment more…',
]

const TYPE_META = {
  trackable:     { label: 'Track',    bg: '#07272D', color: '#fff' },
  informational: { label: 'FYI',      bg: '#1d4ed8', color: '#fff' },
  optional:      { label: 'Optional', bg: '#7a9aaa', color: '#fff' },
}

function TypeBadge({ type, small }) {
  const m = TYPE_META[type] || TYPE_META.trackable
  return (
    <span style={{
      display: 'inline-block',
      background: m.bg, color: m.color,
      borderRadius: 20, padding: small ? '1px 7px' : '2px 10px',
      fontSize: small ? 10 : 11, fontWeight: 600, letterSpacing: '0.04em',
      whiteSpace: 'nowrap', fontFamily: '"DM Sans", sans-serif',
    }}>{m.label}</span>
  )
}

function ItemRow({ item, onChange, expanded, onToggleExpand }) {
  return (
    <div style={{
      background: item.include ? 'var(--aj-bg-elevated)' : 'var(--aj-bg-subtle)',
      border: '0.5px solid var(--aj-border-mid)',
      borderRadius: 6,
      marginBottom: 6,
      opacity: item.include ? 1 : 0.55,
    }}>
      {/* Collapsed row */}
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', cursor: 'pointer' }}
        onClick={onToggleExpand}
      >
        <input
          type="checkbox"
          checked={item.include}
          style={{ flexShrink: 0, width: 15, height: 15, cursor: 'pointer', accentColor: '#07272D' }}
          onClick={e => e.stopPropagation()}
          onChange={e => onChange({ ...item, include: e.target.checked })}
        />
        <TypeBadge type={item.type} small />
        {item.comment_number && (
          <span style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-muted)', flexShrink: 0 }}>
            {item.comment_number}
          </span>
        )}
        <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', flexShrink: 0, minWidth: 84 }}>
          {item.discipline}
        </span>
        <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {item.subject || '(no subject)'}
        </span>
        <span style={{ fontSize: 11, color: 'var(--aj-text-muted)', flexShrink: 0 }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div style={{ padding: '0 12px 12px', borderTop: '0.5px solid var(--aj-border)' }}>
          {/* Full comment text */}
          <div style={{
            fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-mid)',
            background: 'var(--aj-bg-subtle)', borderRadius: 4, padding: '8px 10px',
            marginTop: 10, whiteSpace: 'pre-wrap', lineHeight: 1.55,
          }}>
            {item.comment_text}
          </div>
          {item.rationale && (
            <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 4, fontStyle: 'italic' }}>
              AI: {item.rationale}
            </div>
          )}

          {/* Editable fields */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Subject</span>
              <input
                value={item.subject || ''}
                onChange={e => onChange({ ...item, subject: e.target.value })}
                style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 8px', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', outline: 'none', width: '100%', boxSizing: 'border-box' }}
              />
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Discipline</span>
                <select
                  value={item.discipline || 'General'}
                  onChange={e => onChange({ ...item, discipline: e.target.value })}
                  style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 8px', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', width: '100%' }}
                >
                  {DISCIPLINES.map(d => <option key={d}>{d}</option>)}
                </select>
              </label>
              <label style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Type</span>
                <select
                  value={item.type}
                  onChange={e => {
                    const newType = e.target.value
                    const autoResponse = newType === 'informational' ? 'Thank you for the information.' : null
                    onChange({ ...item, type: newType, response_text: autoResponse })
                  }}
                  style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 8px', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', width: '100%' }}
                >
                  <option value="trackable">Track</option>
                  <option value="informational">FYI</option>
                  <option value="optional">Optional</option>
                </select>
              </label>
            </div>
          </div>

          {/* Response field — shown for informational items */}
          {item.type === 'informational' && (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 10 }}>
              <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Pre-filled response</span>
              <input
                value={item.response_text || ''}
                onChange={e => onChange({ ...item, response_text: e.target.value })}
                style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '5px 8px', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', outline: 'none', width: '100%', boxSizing: 'border-box' }}
              />
            </label>
          )}
        </div>
      )}
    </div>
  )
}

export default function PlanCheckIntakeModal({ reviewId, projectId, onClose, onCommitted }) {
  const [phase, setPhase]       = useState('loading')  // loading|upload|uploading|processing|reviewing|committing
  const [intakeId, setIntakeId] = useState(null)
  const [items, setItems]       = useState([])
  const [meta, setMeta]         = useState({})
  const [resumedAt, setResumedAt] = useState(null)
  const [expandedId, setExpandedId] = useState(null)
  const [saveState, setSaveState] = useState('saved')   // saved | saving | error
  const [statusMsg, setStatusMsg] = useState('')
  const [msgIdx, setMsgIdx]       = useState(0)
  const fileInputRef = useRef(null)
  const saveTimerRef = useRef(null)
  const dragRef      = useRef(null)

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [])

  // Cycle through processing messages so the user knows it's working.
  // Main sequence at 3500ms each; once exhausted, loops through patience messages.
  useEffect(() => {
    if (phase !== 'processing') { setMsgIdx(0); return }
    const total = PROCESSING_MESSAGES.length + PATIENCE_MESSAGES.length
    const id = setInterval(() => {
      setMsgIdx(i => {
        const next = i + 1
        // Inside patience range: loop back to start of patience messages
        if (next >= total) return PROCESSING_MESSAGES.length
        return next
      })
    }, 3500)
    return () => clearInterval(id)
  }, [phase])

  // Load any existing reviewing intake on mount
  useEffect(() => {
    getIntakeForReview(reviewId).then(({ intake }) => {
      if (intake) {
        setIntakeId(intake.id)
        setItems(intake.items || [])
        setMeta({
          backcheck_available:     intake.backcheck_available,
          backcheck_notes:         intake.backcheck_notes,
          letter_date:             intake.letter_date,
          plan_checker_name:       intake.plan_checker_name,
          agency_name:             intake.agency_name,
          agency_reference_number: intake.agency_reference_number,
        })
        setResumedAt(intake.updated_at || intake.created_at)
        setPhase('reviewing')
      } else {
        setPhase('upload')
      }
    }).catch(() => setPhase('upload'))
  }, [reviewId])

  // Auto-save when items/meta change
  const scheduleAutoSave = useCallback((newItems, newMeta, id) => {
    if (!id) return
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    setSaveState('saving')
    saveTimerRef.current = setTimeout(async () => {
      try {
        await savePCIntake(id, newItems, newMeta)
        setSaveState('saved')
      } catch {
        setSaveState('error')
      }
    }, 1500)
  }, [])

  function updateItem(updated) {
    const newItems = items.map(it => it.id === updated.id ? updated : it)
    setItems(newItems)
    scheduleAutoSave(newItems, meta, intakeId)
  }

  async function handleFile(file) {
    if (!file || file.type !== 'application/pdf') {
      await alertDialog({ title: 'Invalid file', message: 'Please select a PDF file.' })
      return
    }
    setPhase('uploading')
    setStatusMsg('Uploading PDF…')
    try {
      const { signedUrl, path, publicUrl } = await getPCIntakeUploadUrl(reviewId, file.name)
      await uploadIntakePdf(signedUrl, file)
      setPhase('processing')
      setStatusMsg('Analyzing letter with AI…')
      const { intake } = await processPCIntake({
        reviewId, projectId,
        storagePath: path,
        pdfUrl: publicUrl,
        intakeId: intakeId || undefined,
      })
      setIntakeId(intake.id)
      setItems(intake.items || [])
      setMeta({
        backcheck_available:     intake.backcheck_available,
        backcheck_notes:         intake.backcheck_notes,
        letter_date:             intake.letter_date,
        plan_checker_name:       intake.plan_checker_name,
        agency_name:             intake.agency_name,
        agency_reference_number: intake.agency_reference_number,
      })
      setResumedAt(null)
      setPhase('reviewing')
    } catch (e) {
      setPhase('upload')
      await alertDialog({ title: 'Error', message: e.message })
    }
  }

  async function handleSaveNow() {
    if (!intakeId) return
    setSaveState('saving')
    try {
      await savePCIntake(intakeId, items, meta)
      setSaveState('saved')
    } catch {
      setSaveState('error')
    }
  }

  async function handleCommit() {
    if (!intakeId) return
    const included = items.filter(it => it.include)
    if (!included.length) {
      await alertDialog({ title: 'No comments selected', message: 'Toggle at least one comment on before committing.' })
      return
    }
    setPhase('committing')
    try {
      // Save latest state first
      await savePCIntake(intakeId, items, meta)
      const { letterId } = await commitPCIntake(intakeId)
      onCommitted && onCommitted(letterId)
      onClose()
    } catch (e) {
      setPhase('reviewing')
      await alertDialog({ title: 'Commit failed', message: e.message })
    }
  }

  // Drag and drop handlers
  function onDragOver(e) { e.preventDefault(); e.stopPropagation() }
  function onDrop(e) {
    e.preventDefault(); e.stopPropagation()
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const includedCount = items.filter(it => it.include).length
  const trackCount = items.filter(it => it.type === 'trackable').length
  const fyiCount   = items.filter(it => it.type === 'informational').length
  const optCount   = items.filter(it => it.type === 'optional').length

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 700,
        background: 'rgba(0,0,0,0.35)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', overflowY: 'auto',
        padding: '32px 16px',
      }}
    >
      <div style={{
        background: 'var(--aj-bg-elevated)', borderRadius: 10,
        width: '100%', maxWidth: 820,
        minHeight: 380,
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px 14px',
          borderBottom: '0.5px solid var(--aj-border)',
        }}>
          <div>
            <div style={{ fontFamily: 'Montserrat, sans-serif', fontSize: 13, fontWeight: 700, color: 'var(--aj-text)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              AI Letter Intake
            </div>
            {meta.agency_name && (
              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 2 }}>
                {meta.agency_name}
                {meta.plan_checker_name && ` · ${meta.plan_checker_name}`}
                {meta.letter_date && ` · ${meta.letter_date}`}
              </div>
            )}
          </div>
          {!['uploading','processing','committing'].includes(phase) && (
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', fontSize: 18, color: 'var(--aj-text-muted)', cursor: 'pointer', lineHeight: 1, padding: 4 }}
            >✕</button>
          )}
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px 24px' }}>

          {/* loading */}
          {phase === 'loading' && (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--aj-text-muted)', fontFamily: '"DM Sans", sans-serif', fontSize: 13 }}>
              Loading…
            </div>
          )}

          {/* upload */}
          {phase === 'upload' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: 'var(--aj-text-mid)' }}>
                Upload the plan check letter PDF. Claude will extract and categorize all comments for your review.
              </div>
              <div
                ref={dragRef}
                onDragOver={onDragOver}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                style={{
                  border: '2px dashed var(--aj-border-mid)',
                  borderRadius: 8,
                  padding: '40px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: 'var(--aj-bg-subtle)',
                  transition: 'border-color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--aj-text)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--aj-border-mid)'}
              >
                <div style={{ fontSize: 28, marginBottom: 10 }}>📄</div>
                <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: 'var(--aj-text)', fontWeight: 500 }}>
                  Drag a PDF here or click to browse
                </div>
                <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', marginTop: 6 }}>
                  The plan check letter from the agency
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                style={{ display: 'none' }}
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f) }}
              />
            </div>
          )}

          {/* uploading */}
          {phase === 'uploading' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, flex: 1, padding: 56 }}>
              <div style={{ width: 38, height: 38, border: '3px solid var(--aj-bg-muted)', borderTopColor: 'var(--aj-text)', borderRadius: '50%', animation: 'aj-spin 0.9s linear infinite' }} />
              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-muted)' }}>Uploading PDF…</div>
              <style>{`@keyframes aj-spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* processing — cycling messages */}
          {phase === 'processing' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 28, flex: 1, padding: 56 }}>
              <div style={{ position: 'relative', width: 52, height: 52 }}>
                <div style={{ position: 'absolute', inset: 0, border: '3px solid var(--aj-bg-muted)', borderRadius: '50%' }} />
                <div style={{ position: 'absolute', inset: 0, border: '3px solid transparent', borderTopColor: 'var(--aj-text)', borderRadius: '50%', animation: 'aj-spin 0.9s linear infinite' }} />
                <div style={{ position: 'absolute', inset: 8, border: '2px solid transparent', borderTopColor: 'var(--aj-text-muted)', borderRadius: '50%', animation: 'aj-spin 1.4s linear infinite reverse' }} />
              </div>
              <div style={{ textAlign: 'center', maxWidth: 300 }}>
                <div style={{
                  fontFamily: '"DM Sans", sans-serif', fontSize: 14, color: 'var(--aj-text)', fontWeight: 500,
                  minHeight: 22, transition: 'opacity 0.3s',
                }}>
                  {msgIdx < PROCESSING_MESSAGES.length
                    ? PROCESSING_MESSAGES[msgIdx]
                    : PATIENCE_MESSAGES[(msgIdx - PROCESSING_MESSAGES.length) % PATIENCE_MESSAGES.length]}
                </div>
                <div style={{ fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 8 }}>
                  {msgIdx < PROCESSING_MESSAGES.length
                    ? `step ${msgIdx + 1} of ${PROCESSING_MESSAGES.length}`
                    : 'still working…'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 5, marginTop: 4 }}>
                {PROCESSING_MESSAGES.map((_, i) => (
                  <div key={i} style={{
                    width: i === msgIdx ? 18 : 6, height: 6,
                    borderRadius: 3,
                    background: i <= Math.min(msgIdx, PROCESSING_MESSAGES.length - 1) ? 'var(--aj-text)' : 'var(--aj-bg-muted)',
                    transition: 'all 0.4s ease',
                  }} />
                ))}
              </div>
              <style>{`@keyframes aj-spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {/* committing */}
          {phase === 'committing' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20, flex: 1, padding: 56 }}>
              <div style={{ width: 38, height: 38, border: '3px solid var(--aj-bg-muted)', borderTopColor: 'var(--aj-text)', borderRadius: '50%', animation: 'aj-spin 0.9s linear infinite' }} />
              <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-muted)' }}>Committing comments to the system…</div>
            </div>
          )}

          {/* reviewing */}
          {phase === 'reviewing' && (
            <>
              {/* Resumed banner */}
              {resumedAt && (
                <div style={{
                  background: 'rgba(26,122,74,0.15)', border: '0.5px solid rgba(29,158,117,0.4)', borderRadius: 6,
                  padding: '8px 12px', marginBottom: 14,
                  fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#1D9E75',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span>Resumed from previous session</span>
                  <button
                    onClick={() => { setResumedAt(null); setPhase('upload') }}
                    style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--aj-text-muted)', cursor: 'pointer', textDecoration: 'underline' }}
                  >Re-upload PDF</button>
                </div>
              )}

              {/* Backcheck callout */}
              {meta.backcheck_available && (
                <div style={{
                  background: 'rgba(183,101,26,0.14)', border: '0.5px solid #f59e0b', borderRadius: 6,
                  padding: '10px 14px', marginBottom: 14,
                }}>
                  <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600, color: '#b8540a', marginBottom: 4 }}>
                    📅 Pre-submittal backcheck available
                  </div>
                  {meta.backcheck_notes && (
                    <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#b8540a', fontStyle: 'italic', marginBottom: 6 }}>
                      "{meta.backcheck_notes}"
                    </div>
                  )}
                  <div style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#b8540a' }}>
                    Consider scheduling a pre-submittal meeting with the plan checker before resubmitting.
                  </div>
                </div>
              )}

              {/* Stats bar */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12,
                fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: 'var(--aj-text-muted)',
              }}>
                <span><b style={{ color: 'var(--aj-text)' }}>{items.length}</b> extracted</span>
                {trackCount > 0 && <span><b style={{ color: 'var(--aj-text)' }}>{trackCount}</b> trackable</span>}
                {fyiCount > 0   && <span><b style={{ color: '#1d4ed8' }}>{fyiCount}</b> FYI</span>}
                {optCount > 0   && <span><b style={{ color: 'var(--aj-text-muted)' }}>{optCount}</b> optional</span>}
                <span style={{ flex: 1 }} />
                <button
                  onClick={() => { setItems(items.map(it => ({ ...it, include: true }))); scheduleAutoSave(items.map(it => ({ ...it, include: true })), meta, intakeId) }}
                  style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--aj-text-muted)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                >Select all</button>
                <button
                  onClick={() => { setItems(items.map(it => ({ ...it, include: false }))); scheduleAutoSave(items.map(it => ({ ...it, include: false })), meta, intakeId) }}
                  style={{ background: 'none', border: 'none', fontSize: 11, color: 'var(--aj-text-muted)', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                >Select none</button>
              </div>

              {/* Items list */}
              <div style={{ flex: 1, overflowY: 'auto', maxHeight: 420 }}>
                {items.length === 0 && (
                  <div style={{ textAlign: 'center', padding: 32, color: 'var(--aj-text-muted)', fontFamily: '"DM Sans", sans-serif', fontSize: 13 }}>
                    No items extracted. Try re-uploading the PDF.
                  </div>
                )}
                {items.map(item => (
                  <ItemRow
                    key={item.id}
                    item={item}
                    onChange={updateItem}
                    expanded={expandedId === item.id}
                    onToggleExpand={() => setExpandedId(expandedId === item.id ? null : item.id)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer — shown in reviewing phase */}
        {phase === 'reviewing' && (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 24px', borderTop: '0.5px solid var(--aj-border)',
            background: 'var(--aj-bg-subtle)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                onClick={handleSaveNow}
                style={{
                  fontFamily: '"DM Sans", sans-serif', fontSize: 13,
                  background: 'none', border: '0.5px solid var(--aj-border-mid)',
                  borderRadius: 4, padding: '6px 14px', cursor: 'pointer',
                  color: 'var(--aj-text-mid)',
                }}
              >Save progress</button>
              <span style={{ fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: saveState === 'error' ? '#d97706' : 'var(--aj-text-muted)' }}>
                {saveState === 'saving' ? 'Saving…' : saveState === 'error' ? 'Save failed' : 'Saved'}
              </span>
            </div>
            <button
              onClick={handleCommit}
              disabled={includedCount === 0}
              style={{
                fontFamily: '"DM Sans", sans-serif', fontSize: 13, fontWeight: 600,
                background: includedCount > 0 ? '#07272D' : '#c8d9e0',
                color: '#fff', border: 'none', borderRadius: 4,
                padding: '8px 20px', cursor: includedCount > 0 ? 'pointer' : 'default',
                transition: 'background 0.15s',
              }}
            >
              Commit {includedCount} comment{includedCount !== 1 ? 's' : ''} →
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
